import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { LocalStorageFilterValueTypes } from "components/Organisms/IndexFilters/types";
import { RequestModel, RequestStatus } from "model";
import { useFeatureFlags } from "swing-components";
import { fetchRequestIndex, fetchRequestIndexPaginated } from "utils/api";

import { matchesDate, matchesSchool, matchesStatus } from "./utils";

/** Types */
type ActiveRequests = "active-requests";
type PastRequests = "past-requests";
export type RequestType = ActiveRequests | PastRequests;

/** Constants */
export const SegmentName = {
  Unfilled: "unfilled",
  Waitlist: "waitlist",
  Filled: "filled",
  Completed: "completed",
  Canceled: "canceled",
  Review: "review",
} as const;

export type SegmentNameUnion = (typeof SegmentName)[keyof typeof SegmentName];

export const activeSegments: Array<SegmentNameUnion> = [
  SegmentName.Filled,
  SegmentName.Unfilled,
  SegmentName.Waitlist,
];
export const pastSegments: Array<SegmentNameUnion> = [
  SegmentName.Review,
  SegmentName.Completed,
  SegmentName.Unfilled,
  SegmentName.Canceled,
];

export const doesReqMatchSegment = (segment: SegmentNameUnion, req: RequestModel) => {
  switch (segment) {
    case SegmentName.Unfilled:
      return (
        (req.status === RequestStatus.Submitted && req.poolCount === 0) ||
        req.status === RequestStatus.Closed
      );
    case SegmentName.Waitlist:
      return (
        (req.status === RequestStatus.Submitted ||
          req.status === RequestStatus.ConfirmationPending) &&
        req.poolCount > 0
      );
    case SegmentName.Filled:
      return req.status === RequestStatus.Filled;
    case SegmentName.Completed:
      return req.status === RequestStatus.Completed;
    case SegmentName.Canceled:
      return req.status === RequestStatus.Canceled;
    case SegmentName.Review:
      return req.status === RequestStatus.Completed && !req.hasFeedback;
    default:
      // eslint-disable-next-line no-console
      console.error("Found a request that doesn't match any segments.");
      return;
  }
};

type UseRequestsParams = {
  showHistory?: boolean;
  pageSize?: number;
  filters?: LocalStorageFilterValueTypes;
};

export type UseRequestsReturn = {
  data?: {
    requests: RequestModel[];
    total: number;
    hasNextPage: boolean;
    fetchNextPage: () => Promise<any>;
    isFetchingNextPage: boolean;
  };
  isLoading: boolean;
  isError: boolean;
  error: any;
  refetch: () => Promise<any>;
  isRefetching: boolean;
  isFetching: boolean;
};

// Client-side filtering for legacy API
function applyClientSideFilters(
  requests: RequestModel[],
  filters?: LocalStorageFilterValueTypes,
): RequestModel[] {
  if (!filters) return requests;

  return requests.filter(
    (item) =>
      matchesStatus(item, filters.statuses) &&
      matchesSchool(item, filters.schools) &&
      matchesDate({
        item,
        dateOption: filters?.dateOption,
        customDate: filters?.customDates,
      }),
  );
}

// Main hook that uses feature flag to choose implementation
export function useRequests(params: UseRequestsParams): UseRequestsReturn {
  const { showHistory = false, pageSize = 5, filters } = params;
  const { flags } = useFeatureFlags();

  const usePaginated = flags.enablePaginatedAdminRequestIndexApi;

  // Convert frontend filters to API format
  const convertFiltersForAPI = (filters?: LocalStorageFilterValueTypes) => {
    if (!filters) return undefined;

    const apiFilters: {
      dateOption?: string;
      customStart?: string;
      customEnd?: string;
      statuses?: string[];
      schools?: string[];
    } = {};

    if (filters.dateOption && filters.dateOption !== undefined) {
      apiFilters.dateOption = filters.dateOption;
    }

    if (filters.customDates?.start) {
      apiFilters.customStart = filters.customDates.start;
    }

    if (filters.customDates?.end) {
      apiFilters.customEnd = filters.customDates.end;
    }

    if (filters.statuses && filters.statuses.length > 0) {
      apiFilters.statuses = filters.statuses;
    }

    if (filters.schools && filters.schools.length > 0) {
      apiFilters.schools = filters.schools;
    }

    return apiFilters;
  };

  const apiFilters = convertFiltersForAPI(filters);

  // Single useInfiniteQuery with conditional logic inside
  const query = useInfiniteQuery({
    queryKey: usePaginated
      ? ["requests", !showHistory ? "active" : "past", apiFilters]
      : ["requests-non-paginated", !showHistory ? "active" : "past", filters],
    queryFn: async ({ pageParam = 0 }) => {
      if (usePaginated) {
        return await fetchRequestIndexPaginated({
          showHistory,
          limit: pageSize,
          offset: pageParam,
          filters: apiFilters,
        });
      } else {
        // For non-paginated, ignore pageParam and always fetch all data
        return await fetchRequestIndex({ showHistory });
      }
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!usePaginated) {
        // Non-paginated doesn't support infinite scroll
        return undefined;
      }
      const totalLoaded = allPages.reduce((sum, page) => sum + page.count, 0);
      const hasMore = lastPage?.["has-more"];
      return hasMore ? totalLoaded : undefined;
    },
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    select: (data) => {
      if (usePaginated) {
        // Paginated: data has pages structure
        return {
          requests: data.pages.flatMap((page) =>
            page.data.map((requestData: unknown) => new RequestModel(requestData)),
          ),
          total: data.pages[0]?.total ?? 0,
        };
      } else {
        // Non-paginated: data.pages[0] contains the full response
        const fullResponse = data.pages[0];
        const requests =
          fullResponse?.data?.map((requestData: unknown) => new RequestModel(requestData)) || [];
        const filteredRequests = applyClientSideFilters(requests, filters);
        return {
          requests: filteredRequests,
          total: filteredRequests.length,
        };
      }
    },
  });

  return {
    ...query,
    data: query.data
      ? {
          ...query.data,
          hasNextPage: usePaginated ? query.hasNextPage : false,
          fetchNextPage: usePaginated ? query.fetchNextPage : () => Promise.resolve(),
          isFetchingNextPage: usePaginated ? query.isFetchingNextPage : false,
        }
      : undefined,
  };
}

export function useRequestIndexFilters(requests?: RequestModel[]): RequestModel[] {
  // Since filtering is now done server-side, just return the requests
  // as-is Keep this function for backward compatibility, but it no
  // longer does client-side filtering
  return requests || [];
}
